import type {
	ConversationHistoryPage,
	ConversationSummary,
	MembershipSummary,
} from '@livechat/shared';
import { type FormEvent, useCallback, useEffect, useState } from 'react';
import { io } from 'socket.io-client';

import {
	currentAccessToken,
	listConversationHistory,
	onAccessTokenChanged,
	realtimeUrl,
	refreshSession,
} from '../api.js';
import { assignedTeamMemberLabel, teamMemberRoleLabel } from '../member-label.js';
import { useDateFormat } from '../date-format.js';
import { visitorCompanyLabel } from '../visitor-company.js';
import { MemberAvatar } from './MemberAvatar.js';

const PAGE_SIZE = 25;

function conversationDuration(conversation: ConversationSummary): string {
	const startedAt = new Date(conversation.createdAt).getTime();
	const endedAt = new Date(
		conversation.closedAt ?? conversation.lastMessageAt ?? conversation.createdAt,
	).getTime();
	const minutes = Math.max(0, Math.floor((endedAt - startedAt) / 60_000));
	if (minutes < 1) return '< 1 min';
	if (minutes < 60) return `${minutes} min`;
	const remainingMinutes = minutes % 60;
	return remainingMinutes
		? `${Math.floor(minutes / 60)}h ${remainingMinutes}min`
		: `${Math.floor(minutes / 60)}h`;
}

export function TenantChatHistory({
	initialVisitorId,
	membership,
	onOpenConversation,
}: {
	initialVisitorId?: string;
	membership: MembershipSummary;
	onOpenConversation: (conversationId: string) => void;
}) {
	const { formatDateTime } = useDateFormat();
	const [draftQuery, setDraftQuery] = useState('');
	const [query, setQuery] = useState('');
	const [assignedUserId, setAssignedUserId] = useState('');
	const [classification, setClassification] = useState('');
	const [page, setPage] = useState(1);
	const [result, setResult] = useState<ConversationHistoryPage>({
		agents: [],
		classifications: [],
		items: [],
		page: 1,
		pageSize: PAGE_SIZE,
		total: 0,
	});
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string>();

	const load = useCallback(async () => {
		setLoading(true);
		setError(undefined);
		try {
			setResult(
				await listConversationHistory(membership.tenantId, {
					assignedUserId: assignedUserId || undefined,
					classification: classification || undefined,
					page,
					pageSize: PAGE_SIZE,
					query: query || undefined,
					visitorId: initialVisitorId,
				}),
			);
		} catch (caught) {
			setError(caught instanceof Error ? caught.message : 'Could not load chat history');
		} finally {
			setLoading(false);
		}
	}, [assignedUserId, classification, initialVisitorId, membership.tenantId, page, query]);

	useEffect(() => void load(), [load]);

	useEffect(() => {
		const token = currentAccessToken();
		if (!token) return;
		const socket = io(`${realtimeUrl}/chat`, {
			auth: { accessToken: token, tenantId: membership.tenantId },
			transports: ['websocket', 'polling'],
		});
		const refresh = () => void load();
		let lastRealtimeRefreshAt = 0;
		const stopWatchingToken = onAccessTokenChanged((updatedToken) => {
			socket.auth = { accessToken: updatedToken, tenantId: membership.tenantId };
			if (updatedToken && !socket.connected) socket.connect();
		});
		socket.on('conversation:updated', refresh);
		socket.on('session:error', () => {
			if (Date.now() - lastRealtimeRefreshAt < 60_000) return;
			lastRealtimeRefreshAt = Date.now();
			void refreshSession()
				.then((authentication) => {
					socket.auth = {
						accessToken: authentication.accessToken,
						tenantId: membership.tenantId,
					};
					socket.connect();
				})
				.catch((caught: unknown) =>
					setError(
						caught instanceof Error ? caught.message : 'Realtime connection failed',
					),
				);
		});
		return () => {
			stopWatchingToken();
			socket.disconnect();
		};
	}, [load, membership.tenantId]);

	function search(event: FormEvent<HTMLFormElement>): void {
		event.preventDefault();
		setPage(1);
		setQuery(draftQuery.trim());
	}

	const pageCount = Math.max(1, Math.ceil(result.total / PAGE_SIZE));
	const first = result.total ? (result.page - 1) * result.pageSize + 1 : 0;
	const last = Math.min(result.total, result.page * result.pageSize);
	const agentsById = new Map(result.agents.map((agent) => [agent.id, agent]));

	return (
		<section className="data-page" aria-labelledby="chat-history-heading">
			<header className="subsection-heading history-page-heading">
				<div>
					<h3 id="chat-history-heading">Chat History</h3>
					<p>Search and reopen completed conversations.</p>
				</div>
				<form className="data-search" onSubmit={search} role="search">
					<label className="history-agent-filter">
						<span className="visually-hidden">Filter by agent</span>
						<select
							aria-label="Filter chats by agent"
							onChange={(event) => {
								setPage(1);
								setAssignedUserId(event.currentTarget.value);
							}}
							value={assignedUserId}
						>
							<option value="">All Agents</option>
							{result.agents.map((agent) => (
								<option key={agent.id} value={agent.id}>
									{assignedTeamMemberLabel(
										agent.name,
										agent.memberType,
										'Unassigned',
									)}
								</option>
							))}
						</select>
					</label>
					<label className="history-classification-filter">
						<span className="visually-hidden">Filter by classification</span>
						<select
							aria-label="Filter chats by classification"
							onChange={(event) => {
								setPage(1);
								setClassification(event.currentTarget.value);
							}}
							value={classification}
						>
							<option value="">All Classifications</option>
							{result.classifications.map((item) => (
								<option key={item.id} value={item.name}>
									{item.name}
								</option>
							))}
							<option value="Other">Other</option>
						</select>
					</label>
					<label className="visually-hidden" htmlFor="history-search">
						Search chat history
					</label>
					<input
						aria-label="Search by visitor, company, email, subject, chat ID or message"
						id="history-search"
						onChange={(event) => setDraftQuery(event.currentTarget.value)}
						placeholder="Search chat history…"
						value={draftQuery}
					/>
					<button className="secondary-button compact-button">Search</button>
				</form>
			</header>
			{initialVisitorId && (
				<div className="history-visitor-filter" role="status">
					Showing chat history for this visitor
				</div>
			)}

			{error && <div className="alert error data-page-alert">{error}</div>}
			<div className="data-page-panel paginated-table">
				<div
					className="history-table paginated-table-scroll"
					role="table"
					aria-busy={loading}
				>
					<div className="history-row history-head standard-table-head" role="row">
						<span>Date</span>
						<span>Visitor</span>
						<span>Agent</span>
						<span>Department</span>
						<span>Chat time</span>
						<span>Rating</span>
					</div>
					{result.items.map((conversation) => {
						const agent = conversation.assignedUserId
							? agentsById.get(conversation.assignedUserId)
							: undefined;
						const role = agent?.role
							? teamMemberRoleLabel(agent.memberType, agent.role)
							: conversation.assignedMemberType === 'ai_agent'
								? 'AI Agent'
								: conversation.assignedUserName
									? 'Agent'
									: undefined;
						return (
							<button
								className="history-row history-link"
								key={conversation.id}
								onClick={() => onOpenConversation(conversation.id)}
								role="row"
								type="button"
							>
								<time>
									{formatDateTime(
										conversation.closedAt ??
											conversation.lastMessageAt ??
											conversation.createdAt,
									)}
								</time>
								<span className="history-visitor">
									<span className="history-visitor-name">
										{conversation.visitorName ?? 'Visitor'}
									</span>
									{visitorCompanyLabel({
										company: conversation.visitorCompany,
										name: conversation.visitorName,
									}) && (
										<small>
											{visitorCompanyLabel({
												company: conversation.visitorCompany,
												name: conversation.visitorName,
											})}
										</small>
									)}
								</span>
								<span className="history-agent">
									{agent?.avatarEnabled && agent.avatarPreset && (
										<MemberAvatar
											dataUrl={
												agent.avatarKind === 'uploaded'
													? agent.avatarDataUrl
													: undefined
											}
											displayName={agent.name}
											enabled={agent.avatarEnabled}
											preset={agent.avatarPreset}
											size="small"
										/>
									)}
									<span className="history-agent-copy">
										<span>{conversation.assignedUserName ?? 'Unassigned'}</span>
										{role && <small className="capitalise">{role}</small>}
									</span>
								</span>
								<span>{conversation.departmentName ?? 'General'}</span>
								<span>{conversationDuration(conversation)}</span>
								<span
									aria-label={
										conversation.rating
											? `${conversation.rating} out of 5 stars`
											: 'Not rated'
									}
									className="history-rating"
								>
									{conversation.rating ? `${conversation.rating} / 5` : '—'}
								</span>
							</button>
						);
					})}
					{!loading && !result.items.length && (
						<div className="data-empty">
							<strong>No completed chats found</strong>
							<span>Closed conversations will appear here.</span>
						</div>
					)}
					{loading && !result.items.length && (
						<div className="data-empty">Loading chats…</div>
					)}
				</div>

				<footer className="data-pagination">
					<span>
						{first}–{last} of {result.total}
					</span>
					<div>
						<button
							aria-label="Previous page"
							disabled={page <= 1 || loading}
							onClick={() => setPage((current) => Math.max(1, current - 1))}
						>
							‹
						</button>
						<span aria-live="polite">
							Page {page} of {pageCount}
						</span>
						<button
							aria-label="Next page"
							disabled={page >= pageCount || loading}
							onClick={() => setPage((current) => Math.min(pageCount, current + 1))}
						>
							›
						</button>
					</div>
				</footer>
			</div>
		</section>
	);
}
